import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { useUserStore } from '@/config/store/userStore'

const resource = 'assistant/v2'

export type AssistantAgent = {
  id: string
  label: string
  description?: string
  examplePrompts?: string[]
}

export type AssistantAgentsResponse = {
  mode?: 'auto'
  agents?: AssistantAgent[]
  capabilities?: AssistantAgent[]
  examplePrompts?: string[]
}

export type AskAttachment = {
  name: string
  mimeType: string
  dataBase64: string
}

type AskMessage = {
  role: 'user' | 'assistant'
  content: string
  attachments?: AskAttachment[]
}

type AskPayload = {
  question: string
  language?: string
  messages?: AskMessage[]
  attachments?: AskAttachment[]
  agentId?: string | null
  mentions?: Array<{
    type: string
    id: string
    label?: string
  }>
}

type AskUsage = {
  promptTokens?: number
  inputTokens?: number
  cachedTokens?: number
  completionTokens?: number
  totalTokens?: number
}

export type AskStreamHandlers = {
  onToken?: (text: string) => void
  onEvent?: (event: Record<string, unknown>) => void
  onDone?: (payload: {
    answer: string
    agentId?: string
    usage?: AskUsage
    uiBlocks?: unknown[]
    tools?: Array<{ id: string; name: string; streaming?: boolean }>
  }) => void
  onError?: (message: string) => void
  signal?: AbortSignal
}

const listAgents =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (query: { language?: string } = {}) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/agents`,
      query,
    })
  }

const ask =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (data: AskPayload) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/ask`,
      data,
    })
  }

const applyStaffing =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (data: { previewToken: string }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/staffing/apply`,
      data,
    })
  }

const askStream = () =>
  async (data: AskPayload, handlers: AskStreamHandlers = {}) => {
    const userStore = useUserStore()
    const token = userStore?.token
    if (!token) {
      throw new Error('Missing auth token')
    }

    const response = await fetch(`${BASE_URL}/${API_MAIN_VERSION}/${resource}/ask/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
      signal: handlers.signal,
    })

    if (!response.ok) {
      let message = `Assistant stream failed (${response.status})`
      try {
        const errBody = await response.json()
        if (errBody?.customMessage) message = errBody.customMessage
      }
      catch {
        // ignore parse errors
      }
      throw new Error(message)
    }

    if (!response.body) {
      throw new Error('Assistant stream empty body')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let receivedDone = false

    const handleEvent = (event: any) => {
      if (!event || typeof event !== 'object') return
      if (event.type === 'token' && typeof event.text === 'string') {
        handlers.onToken?.(event.text)
        return
      }
      if (event.type === 'tool' || event.type === 'reasoning' || event.type === 'ui') {
        handlers.onEvent?.(event)
        return
      }
      if (event.type === 'done') {
        receivedDone = true
        handlers.onDone?.({
          answer: event.answer || '',
          agentId: event.agentId,
          usage: event.usage,
          uiBlocks: Array.isArray(event.uiBlocks) ? event.uiBlocks : [],
          tools: Array.isArray(event.tools) ? event.tools : [],
        })
        return
      }
      if (event.type === 'error') {
        handlers.onError?.(event.message || 'Assistant stream failed')
      }
    }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const chunks = buffer.split('\n\n')
      buffer = chunks.pop() || ''
      for (const chunk of chunks) {
        const dataLine = chunk
          .split('\n')
          .map(line => line.trimEnd())
          .find(line => line.startsWith('data:'))
        if (!dataLine) continue
        const raw = dataLine.replace(/^data:\s?/, '')
        if (!raw || raw === '[DONE]') continue
        try {
          handleEvent(JSON.parse(raw))
        }
        catch {
          // ignore malformed SSE frames
        }
      }
    }

    if (buffer.trim()) {
      const dataLine = buffer
        .split('\n')
        .map(line => line.trimEnd())
        .find(line => line.startsWith('data:'))
      if (dataLine) {
        const raw = dataLine.replace(/^data:\s?/, '')
        if (raw && raw !== '[DONE]') {
          try {
            handleEvent(JSON.parse(raw))
          }
          catch {
            // ignore
          }
        }
      }
    }

    if (!receivedDone) {
      throw new Error('Assistant stream ended without done event')
    }
  }

export default (axios: AxiosInstance) => ({
  listAgents: listAgents(useAxios(axios)),
  ask: ask(useAxios(axios)),
  askStream: askStream(),
  applyStaffing: applyStaffing(useAxios(axios)),
})
